"use client";
import { useJourneySession } from "@/hooks/useJourneySession";
import { useLookupStore } from "@/stores/lookupStore";
import { submitLookup } from "@/stores/journey";
import { requestResultsScroll } from "@/utilities/results-scroll";
import { applyOnlineUrl, restartLookupMessage, type LookupInput } from "@/utilities/signup-lookup";

import { RadioGroupField } from "@/components/form/fields/RadioGroupField/RadioGroupField";
import { EnergyType } from "@/types/data";
import { track } from "@/utilities/analytics";
import { siteAPIURL } from "@/utilities/constants";
import { ErrorMessage } from "@hookform/error-message";
import clsx from "clsx";
import { motion } from "framer-motion";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { InputField } from "../../form/fields/InputField/InputField";
import { CheckboxInput } from "../../form/input/CheckboxInput/CheckboxInput";
import { AddressSearchBox } from "../AddressSearchBox/AddressSearchBox";
import { Button } from "../Button/Button";
import { MaterialIcon } from "../MaterialIcon/MaterialIcon";
import { NmiSearchModal } from "../NmiSearchModal/NmiSearchModal";
import styles from "./ProductLookup.module.scss";
import type { AddressSearchSupportContent, NmiSearchModalContent } from "@/sanity/queries";

interface ProductLookupProps {
	className?: string;
	searchParams?: Record<string, string>;
	addressSearchSupportContent?: AddressSearchSupportContent | null;
	nmiSearchModalContent?: NmiSearchModalContent | null;
}

type ProductLookupFormValues = {
	address: string;
	promo: string;
	energy: string;
	hasPromoCode: boolean;
	addressKey: string;
	nmi: string;
};

type AddressSuggestion = {
	text: string;
	globalAddressKey: string;
};

export const ProductLookup = ({
	className,
	searchParams,
	addressSearchSupportContent,
	nmiSearchModalContent,
}: ProductLookupProps) => {
	const router = useRouter();
	const pathname = usePathname();
	const restart = searchParams?.restart === "1";
	const ready = useJourneySession(restart);
	const lookup = useLookupStore(state =>
		pathname.endsWith("/apply-online") && !restart ? state.lookup : null,
	);
	const initialNmi = lookup?.kind === "nmi" ? lookup.nmi : "";
	const initialAddress =
		lookup?.kind === "address" ? lookup.address : initialNmi ? getNmiDisplayValue(initialNmi) : "";

	const formMethods = useForm<ProductLookupFormValues>({
		defaultValues: {
			address: initialAddress,
			promo: searchParams?.promo || "",
			energy: searchParams?.energy || lookup?.energy || "",
			hasPromoCode: searchParams?.promo ? true : false,
			addressKey: lookup?.kind === "address" ? lookup.addressKey : "",
			nmi: initialNmi,
		},
		reValidateMode: "onChange",
		shouldFocusError: false,
	});

	const [address, setAddress] = useState(initialAddress);
	const [nmi, setNmi] = useState(initialNmi);
	const [isNmiModalOpen, setIsNmiModalOpen] = useState(false);

	const {
		handleSubmit,
		formState: { errors },
		register,
		resetField,
		watch,
		setValue,
		setError,
		clearErrors,
	} = formMethods;

	const addressKey = watch("addressKey");
	const energy = watch("energy");
	const promoCode = watch("promo");
	const isPromoChecked = watch("hasPromoCode");
	const watchedNmi = watch("nmi");

	const [formProcessing, setFormProcessing] = useState(false);
	const addressResolution = useRef<AbortController | null>(null);
	const pendingNavigation = useRef<{
		lookup: ReturnType<typeof useLookupStore.getState>["lookup"];
		energy?: string;
		promo?: string;
	} | null>(null);
	useEffect(() => () => addressResolution.current?.abort(), []);

	useEffect(() => {
		if (!ready) return;
		const pending = pendingNavigation.current;
		// Keep the submitted fields while the store has updated but the URL has not.
		if (
			pending &&
			lookup === pending.lookup &&
			searchParams?.energy === pending.energy &&
			searchParams?.promo === pending.promo
		)
			return;
		pendingNavigation.current = null;
		const saved = lookup;
		const nextNmi = saved?.kind === "nmi" ? saved.nmi : "";
		const nextAddress =
			saved?.kind === "address" ? saved.address : nextNmi ? getNmiDisplayValue(nextNmi) : "";
		setNmi(nextNmi);
		setAddress(nextAddress);
		setValue("nmi", nextNmi);
		setValue("address", nextAddress);
		setValue("addressKey", saved?.kind === "address" ? saved.addressKey : "");
		setValue("energy", searchParams?.energy || saved?.energy || "");
		setValue("promo", searchParams?.promo || "");
		setValue("hasPromoCode", Boolean(searchParams?.promo));
		clearErrors("address");
	}, [ready, lookup, pathname, searchParams?.energy, searchParams?.promo, setValue, clearErrors]);

	function navigateToLookup(input: LookupInput) {
		if (!ready) return;
		submitLookup(input);
		pendingNavigation.current = {
			lookup: useLookupStore.getState().lookup,
			energy: searchParams?.energy,
			promo: searchParams?.promo,
		};
		const url = applyOnlineUrl(input.energy, input.promo);
		requestResultsScroll();
		if (pathname.endsWith("/apply-online")) router.replace(url);
		else router.push(url);
		setFormProcessing(false);
		document.getElementById("product-result")?.scrollIntoView();
	}

	useEffect(() => {
		if (watchedNmi && energy === "gas") {
			setNmi("");
			setAddress("");
			setValue("nmi", "");
			setValue("address", "");
			setValue("addressKey", "");
			clearErrors("address");
		}
	}, [clearErrors, energy, setValue, watchedNmi]);

	useEffect(() => {
		if (energy === "gas" && isNmiModalOpen) {
			setIsNmiModalOpen(false);
		}
	}, [energy, isNmiModalOpen]);

	function handleNmiSearch(nmi: string) {
		addressResolution.current?.abort();
		track({ event: "address_search", search_type: "nmi", energy: "electricity" });

		const nmiDisplayValue = getNmiDisplayValue(nmi);

		setNmi(nmi);
		setAddress(nmiDisplayValue);
		setValue("nmi", nmi);
		setValue("address", nmiDisplayValue);
		setValue("addressKey", "");
		setValue("energy", "electricity");
		clearErrors("address");

		navigateToLookup({
			kind: "nmi",
			nmi,
			energy: "electricity",
			promo: promoCode && isPromoChecked ? promoCode : "",
		});
	}

	async function resolveAddressSuggestion(address: string, signal: AbortSignal) {
		const response = await fetch(siteAPIURL + "/signup/address-search", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ address }),
			cache: "no-store",
			signal,
		});
		const data = await response.json();

		if (!response.ok) {
			throw new Error(data?.error || "Address search failed");
		}

		const suggestions = (data?.suggestions || []) as AddressSuggestion[];
		return suggestions[0] || null;
	}

	const onSubmit: Parameters<typeof handleSubmit>[0] = async data => {
		addressResolution.current?.abort();
		const controller = new AbortController();
		addressResolution.current = controller;
		setFormProcessing(true);

		if (data.nmi && data.energy === "electricity") {
			track({ event: "address_search", search_type: "nmi", energy: "electricity" });

			navigateToLookup({
				kind: "nmi",
				nmi: data.nmi,
				energy: "electricity",
				promo: promoCode && isPromoChecked ? promoCode : "",
			});
			return;
		}

		const typedAddress = (address || data.address || "").trim();
		let selectedAddress = typedAddress;
		let selectedAddressKey = data.addressKey || addressKey;

		if (!selectedAddress) {
			setError("address", { message: "Please enter your connection address" });
			setFormProcessing(false);
			return;
		}

		if (!selectedAddressKey) {
			try {
				const suggestion = await resolveAddressSuggestion(selectedAddress, controller.signal);
				if (controller.signal.aborted) return;

				if (!suggestion) {
					track({
						event: "address_search_failed",
						reason: "not_resolved",
						energy: energy as EnergyType,
					});
					setError("address", { message: "Please select a valid address to continue" });
					setFormProcessing(false);
					return;
				}

				selectedAddress = suggestion.text;
				selectedAddressKey = suggestion.globalAddressKey;
				setAddress(selectedAddress);
				setValue("address", selectedAddress);
				setValue("addressKey", selectedAddressKey);
				clearErrors("address");
			} catch {
				if (controller.signal.aborted) return;
				console.error("Failed to search address");
				track({
					event: "address_search_failed",
					reason: "search_error",
					energy: energy as EnergyType,
				});
				setError("address", {
					message: "We couldn't search that address. Please try again.",
				});
				setFormProcessing(false);
				return;
			}
		}

		track({ event: "address_search", search_type: "address", energy: energy as EnergyType });

		navigateToLookup({
			kind: "address",
			address: selectedAddress,
			addressKey: selectedAddressKey,
			energy: energy as EnergyType,
			promo: promoCode && isPromoChecked ? promoCode : "",
		});
	};

	return (
		<div className={styles["product-lookup-wrapper"]}>
			{restart && !lookup && <p role="status">{restartLookupMessage}</p>}
			<FormProvider {...formMethods}>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className={clsx(styles["product-lookup"], className)}
				>
					<AddressSearchBox
						defaultAddress={address}
						nmi={nmi}
						energy={energy}
						addressSearchSupportContent={addressSearchSupportContent}
						isNmiSearchModalOpen={isNmiModalOpen}
						onNmiSearchModalOpenChange={setIsNmiModalOpen}
						onClearClicked={() => {
							addressResolution.current?.abort();
							setFormProcessing(false);
							setValue("address", "");
							setValue("addressKey", "");
							setValue("nmi", "");
							setAddress("");
							setNmi("");
							clearErrors("address");
						}}
						onAddressSelected={(data: any) => {
							addressResolution.current?.abort();
							setFormProcessing(false);
							setValue("address", data.addressText);
							setValue("addressKey", data.globalAddressKey);
							setValue("nmi", "");
							setAddress(data.addressText);
							setNmi("");
							clearErrors("address");
						}}
						onInputChange={value => {
							addressResolution.current?.abort();
							setFormProcessing(false);
							setValue("address", value);
							setValue("addressKey", "");
							setValue("nmi", "");
							setAddress(value);
							setNmi("");
						}}
					/>

					<div className={styles["energy-type"]}>
						<RadioGroupField
							register={register}
							hideError
							className={styles["energy-type__field"]}
							innerClassName={styles["energy-type__field-inner"]}
							label={"Energy type"}
							name="energy"
							registerOptions={{
								required: "Please select an energy type.",
							}}
							options={[
								{
									label: "Electricity",
									value: "electricity",
								},
								{
									label: "Gas",
									value: "gas",
								},
							]}
						/>
					</div>
					<motion.div className={styles["promo"]}>
						<CheckboxInput
							className={styles["promo__checkbox"]}
							label="I have a promo code"
							{...register("hasPromoCode", {
								onChange: () => {
									resetField("promo");
								},
							})}
						/>
						<motion.div
							layout
							initial={{ height: 0, opacity: 0 }}
							animate={
								isPromoChecked
									? { height: "auto", opacity: 1, visibility: "visible" }
									: { height: 0, opacity: 0, visibility: "hidden" }
							}
							className={clsx(styles["promo__input-wrapper"])}
							aria-hidden={!isPromoChecked}
						>
							<span className={styles["promo__icon"]}>
								<MaterialIcon name="shoppingmode" />
							</span>
							<InputField
								register={register}
								inputProps={{ tabIndex: isPromoChecked ? 0 : -1 }}
								className={styles["promo__field"]}
								label="Promo code"
								placeholder="Enter code"
								name="promo"
								registerOptions={{
									required:
										isPromoChecked &&
										"Please enter a promo code or deselect the checkbox to continue",
									disabled: !isPromoChecked,
								}}
								hideError
								hideLabel
							/>
							{promoCode && (
								<button
									className={styles["promo__clear"]}
									aria-label="Clear promo code"
									onClick={() => resetField("promo", { defaultValue: "" })}
									type="button"
								>
									<MaterialIcon name="close" />
								</button>
							)}
						</motion.div>
					</motion.div>

					{Object.entries(errors).length > 0 && (
						<div className={styles["errors"]}>
							<span className={styles["errors__icon"]}>
								<MaterialIcon name="warning" />
							</span>
							<ul>
								{errors.address && (
									<li>
										<ErrorMessage
											errors={errors}
											name="address"
										/>
									</li>
								)}
								{errors.energy && (
									<li>
										<ErrorMessage
											errors={errors}
											name="energy"
										/>
									</li>
								)}
								{errors.promo && (
									<li>
										<ErrorMessage
											errors={errors}
											name="promo"
										/>{" "}
									</li>
								)}
							</ul>
						</div>
					)}
					<Button
						className={styles["product-lookup__submit"]}
						type="submit"
						showArrow
						disabled={formProcessing || !ready}
						label={formProcessing ? "Loading plans" : "See Plan"}
					/>
				</form>
			</FormProvider>
			{energy !== "gas" && (
				<NmiSearchModal
					isOpen={isNmiModalOpen}
					content={nmiSearchModalContent}
					onNmiSearch={handleNmiSearch}
					onOpenChange={setIsNmiModalOpen}
				/>
			)}
		</div>
	);
};

const getNmiDisplayValue = (nmi: string) => `NMI: ${nmi}`;
